import React, { useCallback, useContext } from "react";

import { useTranslation } from "@root/misc/useTranslation";

import { LineupContext } from "../../context/LineupContext";
import { LineupOption, TeamID } from "../../types";
import { usePickerStateSelector } from "../../context/pickerState";

import { useAthleteImgProps } from "../athleteImage";
import { useTeamImgProps } from "../teamImage";
import { StatsBlock, StatsLink } from "../Stats";

import { useHandlePick } from "./Picker";

interface AthleteImageProps {
  athlete: LineupOption;
}

const AthleteImage = ({ athlete }: AthleteImageProps) => {
  const athleteImgProps = useAthleteImgProps();

  return (
    <div className="neo__lineupPicker__option__playerImage">
      <img {...athleteImgProps(athlete)} width={70} height={70} />
    </div>
  );
};

interface TeamImageProps {
  team: TeamID;
}

const TeamImage = ({ team }: TeamImageProps) => {
  const teamImgProps = useTeamImgProps();

  return (
    <div className="neo__lineupPicker__option__teamImage">
      <img {...teamImgProps(team)} width={30} height={30} />
    </div>
  );
};

interface InfoContainerProps {
  option: LineupOption;
}

const InfoContainer = ({ option }: InfoContainerProps) => {
  return (
    <div className="neo__lineupPicker__option__infoContainer">
      <div className="neo__lineupPicker__option__infoRow">
        <div className="neo__lineupPicker__option__infoPrice">
          {option.price}
          <div className="neo__lineupPicker__option__infoPricePuk" />
        </div>
        <div className="neo__lineupPicker__option__infoPosition">
          {option.position}
        </div>
      </div>
      <div className="neo__lineupPicker__option__infoRow">
        <div className="neo__lineupPicker__option__infoName">
          <span>{option.short_name ?? option.name}</span>
        </div>
        <div
          className={
            "neo__lineupPicker__option__infoCountry" +
            (option.german_license
              ? " neo__lineupPicker__option__infoCountry--german"
              : "")
          }
        >
          {option.country_abbr}
        </div>
      </div>
    </div>
  );
};

export const PickerOption = (o: LineupOption) => {
  const openFor = usePickerStateSelector((state) => state.openFor);

  const handlePick = useHandlePick();

  const { isAlreadyInPicks, getSlotIfPicked } = useContext(LineupContext);

  const onClick = useCallback(() => {
    handlePick(o);
  }, [handlePick, o]);

  const currentOptionInSlot =
    (openFor && getSlotIfPicked(openFor.slot)?.pickedOption) || undefined;

  const isAlreadyInLineup = isAlreadyInPicks(o.id);
  const isTheOptionInTheSlotThePickerIsOpenedFor =
    currentOptionInSlot?.id === o.id;

  const className = [
    "neo__lineupPicker__option",
    isAlreadyInLineup ? "neo__lineupPicker__option--alreadyInLineup" : "",
    isTheOptionInTheSlotThePickerIsOpenedFor
      ? "neo__lineupPicker__option--sameAsInThisSlot"
      : "",
  ].join(" ");

  return (
    <a key={o.id} className={className} onClick={onClick}>
      <div className="neo__lineupPicker__option__topContainer">
        <AthleteImage athlete={o} />
        <InfoContainer option={o} />
        <TeamImage team={o.team} />
      </div>
      <div className="neo__lineupPicker__option__bottomContainer">
        <StatsBlock option={o} />
        <StatsLink option={o} />
      </div>
      {(isAlreadyInLineup || isTheOptionInTheSlotThePickerIsOpenedFor) && (
        <div className="neo__lineupPicker__option__check" />
      )}
    </a>
  );
};
